import { Button, notify, useConfirmModal } from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import { DocService } from '@affine/core/modules/doc';
import {
  DocGrantedUsersService,
  type GrantedUser,
  ObjectPermissionMutationService,
} from '@affine/core/modules/permissions';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import {
  createObjectPermissionRoleOptions,
  createWorkspaceCappedDocRoleOptions,
  getObjectPermissionAreaBlockedNotice,
  getWorkspaceRoleCeilingNotice,
  objectPermissionRoleLabel,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import * as styles from '@affine/core/modules/permissions/views/permission-access-list.css';
import { PermissionMemberRow } from '@affine/core/modules/permissions/views/permission-invite-picker';
import { PermissionReviewList } from '@affine/core/modules/permissions/views/permission-review-list';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { useDocPermissionReview } from '@affine/core/modules/permissions/views/use-doc-permission-review';
import { isAppDataChangeCancelled } from '@affine/core/modules/streamlain/native-app';
import { DocRole } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useEffect, useRef, useState } from 'react';

export const DOC_ASSIGNABLE_ROLES = [
  DocRole.Manager,
  DocRole.Editor,
  DocRole.Commenter,
  DocRole.Reader,
] as const;

/** Direct person grants, house guests included, one bounded page at a time. */
export function DocPeoplePanel({
  disabled,
  onTotal,
}: {
  disabled: boolean;
  onTotal(total: number | null): void;
}) {
  const t = useI18n();
  const doc = useService(DocService).doc;
  const service = useService(DocGrantedUsersService);
  const mutations = useService(ObjectPermissionMutationService);
  const canManage = useGuard('Doc_Users_Manage', doc.id);
  const canTransfer = useGuard('Doc_TransferOwner', doc.id);
  const online = useObjectPermissionOnlineOnlyState();
  const { openConfirmModal } = useConfirmModal();
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const review = useDocPermissionReview(
    service,
    search,
    undefined,
    Boolean(canManage),
    online.offline
  );
  const total = search ? null : (review.page?.totalCount ?? null);
  useEffect(() => {
    if (total !== null) onTotal(total);
  }, [onTotal, total]);
  const locked =
    disabled || online.disabled || pending || review.loading || review.error;
  const change = async (
    user: GrantedUser,
    action: 'grant' | 'update' | 'revoke',
    role?: DocRole
  ) => {
    if (locked || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await mutations.execute({
        action,
        principal: { id: user.user.id, kind: 'user' },
        role,
        target: { id: doc.id, kind: 'doc' },
      });
      if (action === 'grant' || action === 'revoke') review.first();
      else review.reload();
      service.reset();
    } catch (error) {
      if (!isAppDataChangeCancelled(error))
        notify.error({
          title:
            t['com.streamlain.share-menu.doc-permissions.mutation-failed'](),
        });
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  const selectRole = (user: GrantedUser, role: DocRole) => {
    if (role !== DocRole.Owner) {
      void change(user, 'update', role);
      return;
    }
    openConfirmModal({
      title:
        t[
          'com.streamlain.share-menu.member-management.set-as-owner.confirm.title'
        ](),
      description:
        t[
          'com.streamlain.share-menu.member-management.set-as-owner.confirm.description'
        ](),
      confirmText: t['Confirm'](),
      cancelText: t['Cancel'](),
      onConfirm: () => change(user, 'update', role),
    });
  };
  const rows = review.page?.edges.map(edge => edge.node) ?? [];
  return (
    <>
      <div className={reviewStyles.notice}>
        {t['com.streamlain.permissions.review.direct-note']()}
      </div>
      <PermissionReviewList
        testId="doc-permission-member"
        search={search}
        onSearchChange={setSearch}
        placeholder={t['com.streamlain.permissions.review.search']()}
        loading={review.loading}
        error={review.error}
        offline={online.offline}
        onRetry={review.reload}
        total={review.page?.totalCount ?? null}
        start={review.start}
        count={rows.length}
        onPrevious={review.previous}
        onNext={review.next}
      >
        {rows.map(user => {
          const owner = user.role === DocRole.Owner;
          const candidate = user.hasDirectGrant === false;
          const notice = getObjectPermissionAreaBlockedNotice(t, user);
          const ceiling =
            user.membershipKind === 'house_guests'
              ? null
              : getWorkspaceRoleCeilingNotice(
                  t,
                  user.workspaceRole,
                  DOC_ASSIGNABLE_ROLES
                );
          const options = (
            user.membershipKind === 'house_guests'
              ? createObjectPermissionRoleOptions(t, [
                  DocRole.Reader,
                  DocRole.Editor,
                ])
              : createWorkspaceCappedDocRoleOptions(
                  t,
                  [...DOC_ASSIGNABLE_ROLES, DocRole.Owner],
                  user.workspaceRole
                )
          ).map(option =>
            option.role === DocRole.Owner
              ? {
                  ...option,
                  disabled: option.disabled || !canTransfer,
                }
              : option
          );
          return (
            <div
              key={user.user.id}
              className={styles.accessRowFrame}
              data-area-blocked={user.isAreaBlocked ? 'true' : undefined}
            >
              <PermissionMemberRow
                user={user.user}
                dataTestId={`share-menu-member-item-${user.user.id}`}
                subtitle={user.user.email}
                badges={
                  user.membershipKind === 'house_guests' ? (
                    <span className={styles.guestBadge}>
                      {t[
                        'com.streamlain.share-menu.member-management.guest-badge'
                      ]()}
                    </span>
                  ) : null
                }
                roleSelector={
                  candidate ? (
                    <AddPersonActions
                      user={user}
                      disabled={locked || !canManage}
                      onAdd={role => change(user, 'grant', role)}
                    />
                  ) : owner || !canManage ? (
                    <span className={styles.roleLabel}>
                      {objectPermissionRoleLabel(t, user.role)}
                    </span>
                  ) : (
                    <PermissionRoleMenu
                      role={user.role}
                      options={options}
                      onSelect={role => selectRole(user, role)}
                      disabled={locked}
                      testId={`share-menu-member-role-trigger-${user.user.id}`}
                    />
                  )
                }
                onRemove={
                  !candidate && canManage && !owner
                    ? () =>
                        openConfirmModal({
                          title:
                            t[
                              'com.streamlain.folder-share.remove-user-title'
                            ](),
                          description: t[
                            'com.streamlain.share-menu.doc-permissions.remove-user-description'
                          ]({ name: user.user.name }),
                          confirmText: t['Remove'](),
                          cancelText: t['Cancel'](),
                          confirmButtonOptions: { variant: 'error' },
                          onConfirm: () => change(user, 'revoke'),
                        })
                    : undefined
                }
                removeDisabled={locked}
              />
              {candidate ? (
                <div className={reviewStyles.label}>
                  {t['com.streamlain.permissions.review.candidate']()}
                </div>
              ) : null}
              {user.membershipKind === 'house_guests' &&
              user.houseGuestExpiresAt ? (
                <div className={styles.areaBlockedNotice}>
                  {t[
                    'com.streamlain.share-menu.member-management.guest-expires'
                  ]({
                    date: new Date(user.houseGuestExpiresAt).toLocaleString(),
                  })}
                </div>
              ) : null}
              {notice || ceiling ? (
                <div className={styles.areaBlockedNotice}>
                  {notice ?? ceiling}
                </div>
              ) : null}
            </div>
          );
        })}
      </PermissionReviewList>
    </>
  );
}

function AddPersonActions({
  user,
  disabled,
  onAdd,
}: {
  user: GrantedUser;
  disabled: boolean;
  onAdd(role: DocRole): Promise<void>;
}) {
  const t = useI18n();
  const options = createWorkspaceCappedDocRoleOptions(
    t,
    DOC_ASSIGNABLE_ROLES,
    user.workspaceRole
  );
  const [role, setRole] = useState<DocRole>(
    () =>
      options.find(option => option.role === DocRole.Editor && !option.disabled)
        ?.role ?? DocRole.Reader
  );
  return (
    <div className={reviewStyles.actions}>
      <PermissionRoleMenu
        role={role}
        options={options}
        onSelect={setRole}
        disabled={disabled}
      />
      <Button
        variant="plain"
        disabled={disabled}
        onClick={() => void onAdd(role)}
        aria-label={`${t['com.streamlain.permissions.review.add']()} ${user.user.name}`}
      >
        {t['com.streamlain.permissions.review.add']()}
      </Button>
    </div>
  );
}
